import React from 'react';
import { FlatList } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSelector } from 'react-redux';
import { Trans } from '@lingui/macro';
import { times } from 'lodash';
import styled from 'styled-components/native';

import AuthenticationSendButton from '@src/components/AuthenticationSendButton';
import Button from '@src/components/Button';
import Divider from '@src/components/Divider';
import Image from '@src/components/Image';
import ImageBackground from '@src/components/ImageBackground';
import Row from '@src/components/Row';
import Separator from '@src/components/Separator';
import { Body1, H1 } from '@src/components/Typography';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { padding } from '@src/configuration/constants';
import useBuyPlans from '@src/hooks/useBuyPlans';
import Plan from '@src/screens/PrimePlans/Plan';
import { labelsSelector } from '@src/store/authentication/selectors';
import { PrimePlan } from '@src/types';

import { Props } from './types';
import usePutLabels from './usePutLabels';

const Container = styled.View`
  flex: 1;
`;

const ScrollView = styled.ScrollView`
  flex-grow: 1;
`;

const Title = styled(H1)`
  text-align: center;
  margin: ${padding}px 0;
`;

const Content = styled.View`
  padding: ${padding * 2}px ${padding}px;
`;

const Text = styled(Body1)`
  text-align: center;
`;

const PrimeText = styled(Body1)`
  color: ${({ theme }) => theme.colors.primary};
`;

const Gradient = styled(LinearGradient)`
  height: 100%;
`;

const Background = styled(ImageBackground).attrs({
  resizeMode: 'cover',
})`
  width: 100%;
  height: 250px;
`;

const BookCover = styled(Image)`
  width: 150px;
  height: 225px;
  margin: ${padding / 4}px;
`;

const Component: React.FC<Props> = ({
  navigation,
  route: { params = { result: undefined } },
}) => {
  const labels = useSelector(labelsSelector);
  const {
    activePlan,
    plans,
    handleSnapToPlan,
    payForPrimePlan,
    handleBuyPress,
    calculateIndex,
    activePlanIndex,
  } = useBuyPlans(undefined, params.result);
  const putLabels = usePutLabels();

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <AuthenticationSendButton onPress={putLabels}>
          <Trans>Remind me later</Trans>
        </AuthenticationSendButton>
      ),
    });
  }, [navigation, putLabels]);

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <ScrollView>
          <Title>کاربر ویژه یوتوپین باش</Title>

          <Content>
            <Text>
              برای تو که عاشق{' '}
              {labels?.labelsText?.map((label) => (
                <PrimeText key={label}>{label} </PrimeText>
              ))}
              هستی راهکار داریم
            </Text>
          </Content>

          <Divider />

          <Content>
            <Text>
              ۱. استفاده از کلی راهنمای دقیق سفر (با خرید اکانت ويژه میتونی از
              کلی راهنما به مقاصد مختلف استفاده کنی و حتی میتونی راهنماها رو
              دانلود و آفلاین استفاده کنی!)
            </Text>

            <Row>
              {times(6).map((index) => (
                <BookCover
                  key={index}
                  source={{
                    uri: `https://statics.youtopin.com/img/prime/book_${
                      index + 1
                    }.jpg`,
                  }}
                />
              ))}
            </Row>
          </Content>

          <Divider />

          <Content>
            <Text>
              ۲. تخفیف دایمی روی همه تورها (هزینه اکانت ويژه روی تخفیف فقط یک
              تور کامل به شما بر میگرده)
            </Text>

            <Text>برای مثال:</Text>
            <Text>تور ۵ روزه سفر به ناشناخته های بلوچستان</Text>
            <Text>قیمت برای کاربران عادی : ۲.۰۰۰.۰۰۰ تومان</Text>
            <Text>قیمت برای کاربران ويژه :۱.۹۰۰.۰۰۰ تومان</Text>
            <Text>میزان تخفیف برای کاربران ويژه ۱۰۰.۰۰۰ هزار تومان</Text>
          </Content>

          <Divider />

          <Content>
            <Text>
              ۳. راهنمای اختصاصی سفر برای تو (با درخواست تو ما بهت میگیم چه
              زمانی کجا باید سفر بری ! چی بخوری و کجاها اقامت داشته باشی!)
            </Text>
          </Content>
          <Background source={require('@src/assets/images/tourLeader.jpg')}>
            <Gradient
              locations={[0.2, 0.5, 0.8]}
              colors={['#F2F2F2', 'rgba(255,255,255,0)', '#F2F2F2']}
            />
          </Background>

          <Divider />

          <Content>
            <Text>
              ۴. به هر دلیلی اگه راضی نبودی طی ۲۴ ساعت پولت برگشت داده میشه
            </Text>
          </Content>

          <Divider />

          <Content>
            <Text>خرید اکانت ويژه یوتوپین</Text>
            <Separator level={1} />
            {plans.loading ? null : (
              <FlatList<PrimePlan>
                contentContainerStyle={{
                  flexGrow: 1,
                  justifyContent: 'center',
                }}
                extraData={activePlan}
                ItemSeparatorComponent={Separator}
                horizontal
                initialNumToRender={6}
                data={plans.data}
                renderItem={({
                  item,
                  index,
                }: {
                  item: PrimePlan;
                  index: number;
                }) => {
                  const newIndex = calculateIndex(index);

                  return (
                    <Plan
                      active={activePlanIndex === newIndex}
                      data={item}
                      onPress={() => handleSnapToPlan(newIndex)}
                    />
                  );
                }}
              />
            )}
            <Separator level={1} />
            <Button
              mode="contained"
              onPress={handleBuyPress}
              loading={payForPrimePlan.loading}
              disabled={payForPrimePlan.loading}
            >
              <Trans>Buy Subscription</Trans>
            </Button>
          </Content>
        </ScrollView>
      </Container>
    </>
  );
};

Component.displayName = 'PrimePage';

export default React.memo(Component);
